'use client'

import {
    JournalEntry,
    JournalEntryStatus,
    getJournalEntry,
    getDefaultCashAccount,
    updateJournalEntry,
    reviewJournalEntries,
    deleteJournalEntry,
} from '@/lib/api/financial/financial-accounting-api'
import {
    ChartOfAccountsItem,
    getChartOfAccounts,
} from '@/lib/api/financial/financial-chart-of-accounts-api'
import ConfirmModal from '@/components/confirm-modal'
import SearchableSelect, { SearchableSelectOption } from '@/components/searchable-select'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdBookmark,
    MdCheckCircle,
    MdDelete,
    MdEdit,
    MdReceipt,
    MdSave,
    MdSmartToy,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const formatCurrency = (cents: number) =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('pt-BR')

const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('pt-BR')

const STATUS_LABELS: Record<JournalEntryStatus, string> = {
    PENDING: 'Pendente',
    POSTED: 'Lançado',
    REVIEWED: 'Revisado',
}

const STATUS_BADGE: Record<JournalEntryStatus, string> = {
    PENDING: 'badge-warning',
    POSTED: 'badge-info',
    REVIEWED: 'badge-success',
}

const ORIGIN_LABELS: Record<string, string> = {
    AUTOMATIC: 'Regra Automática',
    AI_CLASSIFIED: 'Classificação por IA',
    MANUAL: 'Manual',
}

export default function JournalEntryDetailPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [entry, setEntry] = useState<JournalEntry | null>(null)
    const [loading, setLoading] = useState(true)
    const [reviewing, setReviewing] = useState(false)

    // Edição de classificação (para PENDING)
    const [editing, setEditing] = useState(false)
    const [accounts, setAccounts] = useState<ChartOfAccountsItem[]>([])
    const [debitAccountId, setDebitAccountId] = useState('')
    const [creditAccountId, setCreditAccountId] = useState('')
    const [saving, setSaving] = useState(false)

    // Delete
    const [showDelete, setShowDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (id) loadEntry()
    }, [id])

    const loadEntry = async () => {
        setLoading(true)
        try {
            const res = await getJournalEntry(parseInt(id!, 10))
            if (res.success && res.result) {
                setEntry(res.result)
            } else {
                toast.error('Lançamento não encontrado')
                navigate('/financial/journal-entries')
            }
        } catch {
            toast.error('Erro ao carregar lançamento')
            navigate('/financial/journal-entries')
        } finally {
            setLoading(false)
        }
    }

    const handleReview = async () => {
        if (!entry) return
        setReviewing(true)
        try {
            const res = await reviewJournalEntries([entry.id])
            if (res.success) {
                toast.success('Lançamento revisado')
                loadEntry()
            }
        } catch {
            toast.error('Erro ao revisar')
        } finally {
            setReviewing(false)
        }
    }

    const startEditing = async () => {
        setEditing(true)
        try {
            const [accountsRes, cashRes] = await Promise.all([
                getChartOfAccounts(),
                getDefaultCashAccount(),
            ])
            if (accountsRes.success && accountsRes.result) {
                setAccounts(accountsRes.result.filter(a => a.active))
            }

            // Pré-preencher se já tem linhas
            if (entry?.lines && entry.lines.length > 0) {
                const debit = entry.lines.find(l => l.type === 'DEBIT')
                const credit = entry.lines.find(l => l.type === 'CREDIT')
                if (debit) setDebitAccountId(String(debit.accountId))
                if (credit) setCreditAccountId(String(credit.accountId))
            } else if (cashRes.success && cashRes.result) {
                // Sem linhas: pré-selecionar contrapartida com conta padrão de Caixa
                const isReceivable = entry?.billing?.type === 'RECEIVABLE'
                if (isReceivable) {
                    // Recebimento: D=Caixa, C=usuário escolhe (receita)
                    setDebitAccountId(String(cashRes.result.id))
                } else {
                    // Pagamento: D=usuário escolhe (despesa), C=Caixa
                    setCreditAccountId(String(cashRes.result.id))
                }
            }
        } catch { /* ignore */ }
    }

    const handleSaveClassification = async () => {
        if (!entry || !debitAccountId || !creditAccountId) {
            toast.warning('Selecione as contas de débito e crédito')
            return
        }

        const amount = entry.billing?.amount || entry.lines.reduce(
            (sum, l) => l.type === 'DEBIT' ? sum + l.amount : sum, 0
        )

        if (!amount) {
            toast.warning('Não foi possível determinar o valor do lançamento')
            return
        }

        setSaving(true)
        try {
            const res = await updateJournalEntry(entry.id, {
                status: 'POSTED',
                lines: [
                    { accountId: parseInt(debitAccountId, 10), type: 'DEBIT', amount },
                    { accountId: parseInt(creditAccountId, 10), type: 'CREDIT', amount },
                ],
            })
            if (res.success) {
                toast.success('Lançamento classificado')
                setEditing(false)
                loadEntry()
            } else {
                toast.error(res.error || 'Erro ao salvar')
            }
        } catch {
            toast.error('Erro ao salvar classificação')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!entry) return
        setDeleting(true)
        try {
            const res = await deleteJournalEntry(entry.id)
            if (res.success) {
                toast.success('Lançamento excluído')
                navigate('/financial/journal-entries')
            }
        } catch {
            toast.error('Erro ao excluir')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
            </div>
        )
    }

    if (!entry) return null

    const debitLines = entry.lines.filter(l => l.type === 'DEBIT')
    const creditLines = entry.lines.filter(l => l.type === 'CREDIT')
    const totalDebit = debitLines.reduce((s, l) => s + l.amount, 0)
    const totalCredit = creditLines.reduce((s, l) => s + l.amount, 0)
    const displayAmount = totalDebit || entry.billing?.amount || 0

    return (
        <div className="p-4 md:p-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate('/financial/journal-entries')}>
                        <MdArrowBack size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <MdBookmark className="text-secondary" />
                            Lançamento #{entry.id}
                        </h1>
                        <p className="text-base-content/60 text-sm mt-1">{entry.description}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {entry.status !== 'REVIEWED' && entry.lines.length > 0 && (
                        <button
                            className="btn btn-success btn-sm gap-1"
                            onClick={handleReview}
                            disabled={reviewing}
                        >
                            {reviewing ? <span className="loading loading-spinner loading-xs"></span> : <MdCheckCircle size={16} />}
                            Revisar
                        </button>
                    )}
                    <button
                        className="btn btn-ghost btn-sm text-error"
                        onClick={() => setShowDelete(true)}
                    >
                        <MdDelete size={16} />
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Informações gerais */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">Informações</h2>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-sm text-base-content/60">Data</span>
                            <span className="text-sm font-medium">{formatDate(entry.date)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-base-content/60">Status</span>
                            <span className={`badge badge-sm ${STATUS_BADGE[entry.status]}`}>
                                {STATUS_LABELS[entry.status]}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-base-content/60">Origem</span>
                            <span className="text-sm flex items-center gap-1">
                                {entry.origin === 'AI_CLASSIFIED' && <MdSmartToy size={14} />}
                                {ORIGIN_LABELS[entry.origin] || entry.origin}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-base-content/60">Valor</span>
                            <span className="text-sm font-bold">{formatCurrency(displayAmount)}</span>
                        </div>
                        {entry.accountingRule && (
                            <div className="flex justify-between">
                                <span className="text-sm text-base-content/60">Regra</span>
                                <span className="text-sm">{entry.accountingRule.name}</span>
                            </div>
                        )}
                        {entry.createdBy && (
                            <div className="flex justify-between">
                                <span className="text-sm text-base-content/60">Criado por</span>
                                <span className="text-sm">{entry.createdBy.name}</span>
                            </div>
                        )}
                        {entry.reviewedBy && (
                            <div className="flex justify-between">
                                <span className="text-sm text-base-content/60">Revisado por</span>
                                <span className="text-sm">{entry.reviewedBy.name}</span>
                            </div>
                        )}
                        {entry.reviewedAt && (
                            <div className="flex justify-between">
                                <span className="text-sm text-base-content/60">Revisado em</span>
                                <span className="text-sm">{formatDateTime(entry.reviewedAt)}</span>
                            </div>
                        )}
                        {entry.reviewNotes && (
                            <div>
                                <span className="text-sm text-base-content/60">Notas da revisão</span>
                                <p className="text-sm mt-1 bg-base-200/50 rounded p-2">{entry.reviewNotes}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Cobrança vinculada */}
                {entry.billing && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <MdReceipt size={20} />
                            Cobrança #{entry.billingId}
                        </h2>
                        <div className="space-y-3">
                            {entry.billing.description && (
                                <div className="flex justify-between">
                                    <span className="text-sm text-base-content/60">Descrição</span>
                                    <span className="text-sm">{entry.billing.description}</span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-sm text-base-content/60">Valor</span>
                                <span className="text-sm font-bold">{formatCurrency(entry.billing.amount)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-base-content/60">Tipo</span>
                                <span className="text-sm">{entry.billing.type === 'RECEIVABLE' ? 'A Receber' : 'A Pagar'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-base-content/60">Status</span>
                                <span className="text-sm">{entry.billing.status}</span>
                            </div>
                            {entry.billing.client && (
                                <div className="flex justify-between">
                                    <span className="text-sm text-base-content/60">Cliente</span>
                                    <span className="text-sm">{entry.billing.client.name}</span>
                                </div>
                            )}
                            {entry.billing.supplier && (
                                <div className="flex justify-between">
                                    <span className="text-sm text-base-content/60">Fornecedor</span>
                                    <span className="text-sm">{entry.billing.supplier.tradingName}</span>
                                </div>
                            )}
                            <button
                                className="btn btn-ghost btn-sm btn-block mt-2"
                                onClick={() => navigate(`/financial/billings/${entry.billingId}`)}
                            >
                                Ver cobrança
                            </button>
                        </div>
                    </div>
                )}

                {/* IA */}
                {(entry.aiConfidence !== null || entry.aiReasoning) && (
                    <div className="bg-secondary/5 rounded-xl border border-secondary/30 p-6">
                        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <MdSmartToy size={20} />
                            Classificação por IA
                        </h2>
                        {entry.aiConfidence !== null && (
                            <div className="flex items-center gap-3 mb-3">
                                <span className="text-sm text-base-content/60">Confiança</span>
                                <div className="flex-1">
                                    <progress
                                        className="progress progress-secondary w-full"
                                        value={entry.aiConfidence * 100}
                                        max={100}
                                    ></progress>
                                </div>
                                <span className="text-sm font-bold">{Math.round(entry.aiConfidence * 100)}%</span>
                            </div>
                        )}
                        {entry.aiReasoning && (
                            <div>
                                <span className="text-sm text-base-content/60">Justificativa</span>
                                <p className="text-sm mt-1 bg-base-100 rounded p-3 italic">{entry.aiReasoning}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Partidas */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 lg:col-span-2">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold">Partidas Dobradas</h2>
                        {entry.lines.length === 0 && !editing && (
                            <button className="btn btn-secondary btn-sm gap-1" onClick={startEditing}>
                                <MdEdit size={16} />
                                Classificar
                            </button>
                        )}
                        {entry.lines.length > 0 && !editing && (
                            <button className="btn btn-ghost btn-sm gap-1" onClick={startEditing}>
                                <MdEdit size={16} />
                                Reclassificar
                            </button>
                        )}
                    </div>

                    {entry.lines.length > 0 ? (
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>Conta</th>
                                    <th className="text-right">Débito</th>
                                    <th className="text-right">Crédito</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entry.lines.map(line => (
                                    <tr key={line.id}>
                                        <td>
                                            <span className="font-mono text-secondary text-xs mr-2">{line.account.code}</span>
                                            <span className="text-sm">{line.account.title}</span>
                                        </td>
                                        <td className="text-right font-mono">
                                            {line.type === 'DEBIT' ? formatCurrency(line.amount) : ''}
                                        </td>
                                        <td className="text-right font-mono">
                                            {line.type === 'CREDIT' ? formatCurrency(line.amount) : ''}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="font-bold border-t-2">
                                    <td>TOTAL</td>
                                    <td className="text-right font-mono">{formatCurrency(totalDebit)}</td>
                                    <td className="text-right font-mono">{formatCurrency(totalCredit)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    ) : !editing ? (
                        <div className="bg-warning/10 rounded-lg p-4 text-center">
                            <p className="text-sm text-base-content/70 mb-1">
                                Valor: <span className="font-bold">{formatCurrency(entry.billing?.amount || 0)}</span>
                            </p>
                            <p className="text-xs text-warning">
                                Aguardando classificação das contas de débito e crédito.
                            </p>
                        </div>
                    ) : null}

                    {/* Formulário de classificação */}
                    {editing && (() => {
                        const isReceivable = entry.billing?.type === 'RECEIVABLE'
                        const TYPE_LABELS: Record<string, string> = { ASSET: 'Ativo', LIABILITY: 'Passivo', EQUITY: 'Patrimônio', INCOME: 'Receita', EXPENSE: 'Despesa' }
                        const TYPE_BADGE: Record<string, string> = { ASSET: 'badge-secondary text-white', LIABILITY: 'badge-warning text-white', EQUITY: 'badge-secondary text-white', INCOME: 'badge-success text-white', EXPENSE: 'badge-error text-white' }

                        const accountOptions: SearchableSelectOption[] = accounts.map(a => ({
                            value: String(a.id),
                            label: `${a.code} - ${a.title}`,
                            badge: TYPE_LABELS[a.type] || a.type,
                            badgeClass: TYPE_BADGE[a.type] || 'badge-ghost',
                            group: TYPE_LABELS[a.type] || a.type,
                        }))

                        return (
                        <div className="mt-4 border border-secondary/30 rounded-lg p-4">
                            <h3 className="font-semibold text-sm mb-1">Classificar lançamento</h3>
                            <p className="text-xs text-base-content/50 mb-4">
                                {isReceivable
                                    ? 'Recebimento: Débito (onde o dinheiro entrou) e Crédito (origem da receita)'
                                    : 'Pagamento: Débito (tipo da despesa) e Crédito (de onde saiu o dinheiro)'
                                }
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Conta Débito
                                            <span className="font-normal text-xs text-base-content/50 ml-1">
                                                {isReceivable ? '(Caixa/Banco)' : '(Despesa)'}
                                            </span>
                                        </span>
                                    </label>
                                    <SearchableSelect
                                        options={accountOptions}
                                        value={debitAccountId}
                                        onChange={val => setDebitAccountId(val)}
                                        placeholder="Selecionar conta"
                                        searchPlaceholder="Buscar por código ou nome..."
                                        noResultsText="Nenhuma conta encontrada"
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Conta Crédito
                                            <span className="font-normal text-xs text-base-content/50 ml-1">
                                                {isReceivable ? '(Receita)' : '(Caixa/Banco)'}
                                            </span>
                                        </span>
                                    </label>
                                    <SearchableSelect
                                        options={accountOptions}
                                        value={creditAccountId}
                                        onChange={val => setCreditAccountId(val)}
                                        placeholder="Selecionar conta"
                                        searchPlaceholder="Buscar por código ou nome..."
                                        noResultsText="Nenhuma conta encontrada"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-4">
                                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Cancelar</button>
                                <button
                                    className="btn btn-secondary btn-sm gap-1"
                                    onClick={handleSaveClassification}
                                    disabled={saving}
                                >
                                    {saving ? <span className="loading loading-spinner loading-xs"></span> : <MdSave size={16} />}
                                    Salvar
                                </button>
                            </div>
                        </div>
                        )
                    })()}
                </div>
            </div>

            <ConfirmModal
                isOpen={showDelete}
                onClose={() => setShowDelete(false)}
                onConfirm={handleDelete}
                title="Excluir Lançamento"
                message="Tem certeza de que deseja excluir este lançamento contábil?"
                loading={deleting}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
